import { z } from "zod";
// ===== ENUMS (converted to const arrays + z.enum + type alias pattern) =====
// TimecardStatus
export const TIMECARD_STATUSES = [
    "PENDING_SUBMISSION",
    "APPROVAL_PENDING",
    "APPROVED",
    "RETURNED",
    "PAID",
    "ISSUE",
    "CHANGES_REQUESTED",
    "MISSING_TIMECARD", // Virtual status, not stored in DB
    "NOT_SCHEDULED", // Virtual status: employee had no shift in this period
    "UNPLANNED_SHIFT_REJECTED",
    "EARLY_CLOCK_IN_REJECTED",
];
export const TimecardStatusSchema = z.enum(TIMECARD_STATUSES);
// Enum-like object for backward compatibility
export const TimecardStatus = {
    PENDING_SUBMISSION: "PENDING_SUBMISSION",
    APPROVAL_PENDING: "APPROVAL_PENDING",
    APPROVED: "APPROVED",
    RETURNED: "RETURNED",
    PAID: "PAID",
    ISSUE: "ISSUE",
    CHANGES_REQUESTED: "CHANGES_REQUESTED",
    MISSING_TIMECARD: "MISSING_TIMECARD",
    NOT_SCHEDULED: "NOT_SCHEDULED",
    UNPLANNED_SHIFT_REJECTED: "UNPLANNED_SHIFT_REJECTED",
    EARLY_CLOCK_IN_REJECTED: "EARLY_CLOCK_IN_REJECTED"
};
// TimecardIssueType
export const TIMECARD_ISSUE_TYPES = [
    // Clock Issues
    "MISSING_CLOCK_OUT",
    "MISSING_CLOCK_IN",
    "OVERLAPPING_ENTRIES",
    "BREAK_EXCEEDED",
    "INVALID_TIMES",
    // System Issues
    "SYNC_FAILURE",
    "INTEGRATION_ERROR",
    "DATA_CORRUPTION",
    // Other
    "OTHER",
];
export const TimecardIssueTypeSchema = z.enum(TIMECARD_ISSUE_TYPES);
// Enum-like object for backward compatibility
export const TimecardIssueType = {
    MISSING_CLOCK_OUT: "MISSING_CLOCK_OUT",
    MISSING_CLOCK_IN: "MISSING_CLOCK_IN",
    OVERLAPPING_ENTRIES: "OVERLAPPING_ENTRIES",
    BREAK_EXCEEDED: "BREAK_EXCEEDED",
    INVALID_TIMES: "INVALID_TIMES",
    SYNC_FAILURE: "SYNC_FAILURE",
    INTEGRATION_ERROR: "INTEGRATION_ERROR",
    DATA_CORRUPTION: "DATA_CORRUPTION",
    OTHER: "OTHER",
};
// TimecardEntryType
export const TIMECARD_ENTRY_TYPES = ["WORK", "BREAK"];
export const TimecardEntryTypeSchema = z.enum(TIMECARD_ENTRY_TYPES);
// Enum-like object for backward compatibility
export const TimecardEntryType = {
    WORK: "WORK",
    BREAK: "BREAK",
};
// BreakType
export const BREAK_TYPES = ["PAID", "UNPAID"];
export const BreakTypeSchema = z.enum(BREAK_TYPES);
// Enum-like object for backward compatibility
export const BreakType = {
    PAID: "PAID",
    UNPAID: "UNPAID",
};
// ChangeRequestStatus
export const CHANGE_REQUEST_STATUSES = [
    "PENDING",
    "APPROVED",
    "REJECTED",
];
export const ChangeRequestStatusSchema = z.enum(CHANGE_REQUEST_STATUSES);
// Enum-like object for backward compatibility
export const ChangeRequestStatus = {
    PENDING: "PENDING",
    APPROVED: "APPROVED",
    REJECTED: "REJECTED",
};
// AutoApproveSetting
export const AUTO_APPROVE_SETTINGS = ["NEVER", "ALWAYS", "CONDITION"];
export const AutoApproveSettingSchema = z.enum(AUTO_APPROVE_SETTINGS);
// Enum-like object for backward compatibility
export const AutoApproveSetting = {
    NEVER: "NEVER",
    ALWAYS: "ALWAYS",
    CONDITION: "CONDITION",
};
// AutoApproveConditionType
export const AUTO_APPROVE_CONDITION_TYPES = [
    "PERCENT_VARIANCE",
    "MINUTE_VARIANCE",
];
export const AutoApproveConditionTypeSchema = z.enum(AUTO_APPROVE_CONDITION_TYPES);
// Enum-like object for backward compatibility
export const AutoApproveConditionType = {
    PERCENT_VARIANCE: "PERCENT_VARIANCE",
    MINUTE_VARIANCE: "MINUTE_VARIANCE",
};
// ===== ZOD SCHEMAS =====
// Settings
export const TimecardSettingsSchema = z.object({
    autoApprove: AutoApproveSettingSchema,
    conditionType: AutoApproveConditionTypeSchema.optional(),
    conditionValue: z.number().optional(),
});
// GPS location schema (reusable)
export const GpsLocationSchema = z.object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracy: z.number().min(0).optional(), // Accuracy in meters
});
// Inputs
export const CreateTimecardInputSchema = z.object({
    shiftId: z.uuid(),
    locationId: z.uuid(),
    clockIn: z.string().datetime(), // ISO string
    // GPS location captured at clock in
    clockInLatitude: z.number().min(-90).max(90).optional(),
    clockInLongitude: z.number().min(-180).max(180).optional(),
    clockInAccuracy: z.number().min(0).optional(), // Accuracy in meters
});
export const UpdateEntryInputSchema = z.object({
    timecardId: z.uuid(),
    clockOut: z.string().datetime(),
    // GPS location captured at clock out
    clockOutLatitude: z.number().min(-90).max(90).optional(),
    clockOutLongitude: z.number().min(-180).max(180).optional(),
    clockOutAccuracy: z.number().min(0).optional(), // Accuracy in meters
});
export const StartBreakInputSchema = z.object({
    timecardId: z.uuid(),
    startTime: z.string().datetime(),
    breakType: BreakTypeSchema.optional().default("UNPAID"),
});
export const EndBreakInputSchema = z.object({
    timecardId: z.uuid(),
    endTime: z.string().datetime(),
});
export const ReturnTimecardInputSchema = z.object({
    reason: z.string().min(1),
    managerComments: z.string().optional(),
});
// Restart Approval Input
export const RestartApprovalInputSchema = z.object({
    // Required reason for restarting approval
    reason: z.string().min(1).max(500),
});
// Payment method options for manual payments
export const PAYMENT_METHODS = [
    "CASH",
    "CASHAPP",
    "DIRECT_DEPOSIT",
    "OTHER",
];
export const PaymentMethodSchema = z.enum(PAYMENT_METHODS);
// Manual payment reason options
export const MANUAL_PAYMENT_REASONS = [
    "TERMINATION_FINAL_PAY",
    "WAGE_ADVANCE",
    "PAYROLL_ERROR_CORRECTION",
];
export const ManualPaymentReasonSchema = z.enum(MANUAL_PAYMENT_REASONS);
// Mark as Paid Input
export const MarkAsPaidInputSchema = z.object({
    paymentDate: z.iso.datetime(),
    paymentMethod: PaymentMethodSchema,
    manualPaymentReason: ManualPaymentReasonSchema,
    grossWages: z.number().min(0),
    taxAmountWithheld: z.number().min(0).optional().default(0),
    netPay: z.number().min(0),
    termsAccepted: z.literal(true, {
        message: "You must accept the terms and conditions",
    }),
    notes: z.string().max(500).optional(),
});
export const EntryChangeSchema = z
    .object({
    timecardEntryId: z.uuid(),
    requestedClockIn: z.iso.datetime().optional(),
    requestedClockOut: z.iso.datetime().optional(),
})
    .refine((data) => data.requestedClockIn !== undefined ||
    data.requestedClockOut !== undefined, {
    message: "At least one of requestedClockIn or requestedClockOut must be provided",
});
export const ShiftChangeSchema = z
    .object({
    requestedShiftStartTime: z.iso.datetime().optional(),
    requestedShiftEndTime: z.iso.datetime().optional(),
})
    .refine((data) => data.requestedShiftStartTime !== undefined ||
    data.requestedShiftEndTime !== undefined, {
    message: "At least one of requestedShiftStartTime or requestedShiftEndTime must be provided",
});
export const ChangeRequestInputSchema = z
    .object({
    timecardId: z.uuid(),
    entries: z.array(EntryChangeSchema).max(10).default([]),
    shiftChange: ShiftChangeSchema.optional(),
    reason: z.string().min(1).max(500),
})
    .refine((data) => data.entries.length > 0 || data.shiftChange !== undefined, {
    message: "At least one entry change or shift change must be provided",
});
export const ProcessChangeRequestInputSchema = z.object({
    status: z.enum(["APPROVED", "REJECTED"]),
    managerComments: z.string().optional(),
});
export const FlagIssueInputSchema = z.object({
    issueType: TimecardIssueTypeSchema,
    issueDetails: z.string().max(2000).optional(), // Additional context about the issue
    comment: z.string().max(1000).optional(),
});
export const ResolveIssueInputSchema = z.object({
    resolution: z.string().min(1).max(1000), // What was done to fix it
});
// Query schemas (companyId removed - comes from JWT)
export const TimecardDashboardQuerySchema = z.object({
    locationId: z.uuid().optional(),
    start: z.string(), // Date string
    end: z.string(), // Date string
});
export const EmployeeTimecardQuerySchema = z.object({
    start: z.string(),
    end: z.string(),
});
// ===== RESPONSE SCHEMAS =====
export const TimecardEntryResponseSchema = z.object({
    id: z.uuid(),
    type: TimecardEntryTypeSchema,
    breakType: BreakTypeSchema.nullable(),
    clockIn: z.iso.datetime(),
    clockOut: z.iso.datetime().nullable(),
    clockInLatitude: z.number().nullable(),
    clockInLongitude: z.number().nullable(),
    clockInAccuracy: z.number().nullable(),
    clockOutLatitude: z.number().nullable(),
    clockOutLongitude: z.number().nullable(),
    clockOutAccuracy: z.number().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const TimecardChangeRequestResponseSchema = z.object({
    id: z.uuid(),
    status: ChangeRequestStatusSchema,
    requestedClockIn: z.iso.datetime().nullable(),
    requestedClockOut: z.iso.datetime().nullable(),
    reason: z.string(),
    managerComments: z.string().nullable(),
    processedByName: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    timecardEntryId: z.uuid().nullable(),
    shiftId: z.uuid().nullable(),
});
export const TimecardApproverSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    userId: z.uuid(),
});
export const TimecardResponseSchema = z.object({
    id: z.uuid(),
    status: TimecardStatusSchema,
    regularHours: z.number(),
    overtimeHours: z.number(),
    holidayHours: z.number(),
    issueType: TimecardIssueTypeSchema.optional(),
    issueDetails: z.string().optional(),
    issueAt: z.iso.datetime().optional(),
    issuedBy: z.string().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    employeeId: z.uuid(),
    locationId: z.uuid(),
    shiftId: z.uuid(),
    managerRequested: z.boolean().optional(),
    entries: z.array(TimecardEntryResponseSchema),
    changeRequests: z.array(TimecardChangeRequestResponseSchema),
    employee: z
        .object({
        id: z.uuid(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
    })
        .optional(),
    shift: z
        .object({
        id: z.uuid(),
        startTime: z.iso.datetime(),
        endTime: z.iso.datetime(),
        locationId: z.uuid(),
        jobRoleFamilyId: z.uuid(),
        jobRoleFamilyName: z.string(),
        jobRoleIds: z.array(z.uuid()),
    })
        .optional(),
});
export const TimecardSummarySchema = z.object({
    id: z.uuid(),
    employeeId: z.uuid(),
    employeeName: z.string(),
    status: TimecardStatusSchema,
    scheduledDuration: z.number(),
    actualDuration: z.number(),
    varianceMinutes: z.number(),
    regularHours: z.number(),
    overtimeHours: z.number(),
    holidayHours: z.number(),
    ptoHours: z.number(),
    shiftStart: z.iso.datetime(),
    shiftEnd: z.iso.datetime(),
    locationAddress: z.string(),
    locationId: z.union([z.uuid(), z.literal("")]),
    approvers: z.array(TimecardApproverSchema),
    timecardIds: z.array(z.uuid()),
    exportedToPayroll: z.enum(["all", "some", "none"]),
    exportInfo: z.object({
        exportId: z.uuid(),
        exportedAt: z.iso.datetime(),
        exportedByName: z.string(),
        s3DownloadUrl: z.string().nullable(),
    }).nullable(),
});
export const TimecardEntryForGraphSchema = z.object({
    type: TimecardEntryTypeSchema,
    breakType: BreakTypeSchema.nullable().optional(),
    clockIn: z.iso.datetime(),
    clockOut: z.iso.datetime().nullable(),
});
export const ScheduledBreakSchema = z.object({
    startTime: z.iso.datetime(),
    endTime: z.iso.datetime(),
    isPaid: z.boolean().optional(),
});
// Pending change request data for chart visualization
export const RequestedBreakChangeSchema = z.object({
    entryId: z.uuid(),
    requestedStart: z.iso.datetime().nullable(),
    requestedEnd: z.iso.datetime().nullable(),
});
export const PendingChangeRequestForChartSchema = z.object({
    // Work entry requested changes (for top bar - actuals)
    requestedClockIn: z.iso.datetime().nullable(),
    requestedClockOut: z.iso.datetime().nullable(),
    // Break entry requested changes
    requestedBreaks: z.array(RequestedBreakChangeSchema).default([]),
    // Shift (schedule) requested changes (for bottom bar)
    requestedShiftStart: z.iso.datetime().nullable(),
    requestedShiftEnd: z.iso.datetime().nullable(),
});
export const EmployeeTimecardDetailRowSchema = z.object({
    id: z.uuid(),
    shiftId: z.uuid().nullable(),
    date: z.iso.datetime(),
    dayOfWeek: z.string(),
    locationName: z.string(),
    locationTimezone: z.string().nullable(),
    locationAddressLine1: z.string().nullable(),
    jobRoleFamilyName: z.string().nullable(),
    status: TimecardStatusSchema,
    managerRequested: z.boolean().optional(),
    scheduledClockIn: z.iso.datetime(),
    scheduledClockOut: z.iso.datetime(),
    actualClockIn: z.iso.datetime().nullable(),
    actualClockOut: z.iso.datetime().nullable(),
    clockInVarianceMinutes: z.number().nullable(),
    clockOutVarianceMinutes: z.number().nullable(),
    breakDurationMinutes: z.number(),
    regularHours: z.number(),
    overtimeHours: z.number(),
    holidayHours: z.number(),
    entries: z.array(TimecardEntryForGraphSchema),
    scheduledBreaks: z.array(ScheduledBreakSchema).optional(),
    // Pending change request data for chart visualization (only present when status is CHANGES_REQUESTED)
    pendingChangeRequest: PendingChangeRequestForChartSchema.nullable().optional(),
    exportedToPayroll: z.boolean(),
    exportInfo: z.object({
        exportId: z.uuid(),
        exportedAt: z.iso.datetime(),
        s3DownloadUrl: z.string().nullable(),
    }).nullable(),
    hasAdjustment: z.boolean(),
});
export const WeeklyHoursSummarySchema = z.object({
    periodStart: z.iso.datetime(),
    periodEnd: z.iso.datetime(),
    regularHours: z.number().nonnegative(),
    overtimeHours: z.number().nonnegative(),
    holidayHours: z.number().nonnegative(),
});
export const EmployeeTimecardSummarySchema = z.object({
    employeeId: z.uuid(),
    employeeName: z.string(),
    regularHours: z.number(),
    overtimeHours: z.number(),
    holidayHours: z.number(),
    ptoHours: z.number(),
    totalHours: z.number(),
    weeklyHoursSummaries: z.array(WeeklyHoursSummarySchema).optional(),
    rows: z.array(EmployeeTimecardDetailRowSchema),
});
// Active timecard response wrapper
export const ActiveTimecardResponseSchema = z.object({
    success: z.boolean(),
    data: TimecardResponseSchema.nullable(),
    compensationType: z.enum(["SALARIED", "HOURLY"]).nullable(),
});
// Legacy interfaces removed - use schema-inferred types above for oRPC
// Reason for timecard edits
export const TIMECARD_EDIT_REASONS = [
    "MANAGER_REQUESTED",
    "EMPLOYEE_SENT_HOME",
    "USER_MISTAKE",
    "SYSTEM_ERROR",
    "OTHER",
];
export const TimecardEditReasonSchema = z.enum(TIMECARD_EDIT_REASONS);
// Break edit input for editing existing breaks (kept for backward compatibility)
export const BreakEditSchema = z.object({
    entryId: z.uuid(),
    breakStart: z.iso.datetime(),
    breakEnd: z.iso.datetime(),
});
// Break edit operations - discriminated union for update, add, and remove
export const BreakUpdateSchema = z.object({
    action: z.literal("update"),
    entryId: z.uuid(),
    breakStart: z.iso.datetime(),
    breakEnd: z.iso.datetime(),
    breakType: BreakTypeSchema.optional(),
});
export const BreakAddSchema = z.object({
    action: z.literal("add"),
    breakStart: z.iso.datetime(),
    breakEnd: z.iso.datetime(),
    breakType: BreakTypeSchema,
});
export const BreakRemoveSchema = z.object({
    action: z.literal("remove"),
    entryId: z.uuid(),
});
export const BreakEditOperationSchema = z.discriminatedUnion("action", [
    BreakUpdateSchema,
    BreakAddSchema,
    BreakRemoveSchema,
]);
// Edit timecard input - for managers to retroactively edit timecard entries and shift times
export const EditTimecardInputSchema = z.object({
    clockIn: z.iso.datetime().optional(),
    clockOut: z.iso.datetime().optional(),
    timecardReason: TimecardEditReasonSchema.optional(),
    breaks: z.array(BreakEditOperationSchema).optional(),
    shiftStartTime: z.iso.datetime().optional(),
    shiftEndTime: z.iso.datetime().optional(),
    shiftReason: TimecardEditReasonSchema.optional(),
    comment: z.string().min(1).max(1000),
    locationId: z.uuid().optional(),
    jobRoleFamilyId: z.uuid().optional(),
    jobRoleIds: z.array(z.uuid()).max(50).optional(),
});
// ===== AUDIT LOG TYPES =====
// Timecard Audit Actions - matches the actions stored in TimecardAuditLog.action
export const TIMECARD_AUDIT_ACTIONS = [
    "CLOCK_IN",
    "CLOCK_OUT",
    "START_BREAK",
    "END_BREAK",
    "SUBMIT",
    "MANAGER_SUBMIT",
    "APPROVE",
    "RETURN",
    "FLAG_ISSUE",
    "RESOLVE_ISSUE",
    "MANAGER_EDIT",
    "MARK_PAID",
    "RESTART_APPROVAL",
    "CHANGE_REQUEST_SUBMITTED",
    "CHANGE_REQUEST_APPROVED",
    "CHANGE_REQUEST_REJECTED",
    "ADMIN_CLOCK_IN",
    "ADMIN_CLOCK_OUT",
    "UNPLANNED_SHIFT_REJECTED",
    "EARLY_CLOCK_IN_REJECTED",
    "RETROACTIVE_SHIFT_CREATED",
    "CREATE_FROM_SHIFT",
    "AUTO_APPROVE",
    "TIMECARD_DELETED",
    "PAYROLL_EXPORT",
    "TIMECARD_ADJUSTMENT",
];
export const TimecardAuditActionSchema = z.enum(TIMECARD_AUDIT_ACTIONS);
// Query input for fetching audit logs
export const TimecardAuditLogQuerySchema = z.object({
    timecardIds: z.array(z.uuid()).min(1).max(1000),
    limit: z.number().int().min(1).max(500).optional().default(100),
    offset: z.number().int().min(0).optional().default(0),
});
// Performer info schema (user who performed the action)
export const TimecardAuditPerformerSchema = z.object({
    userId: z.uuid(),
    firstName: z.string(),
    lastName: z.string(),
});
// Single audit log response
export const TimecardAuditLogResponseSchema = z.object({
    id: z.uuid(),
    timecardId: z.uuid().nullable(),
    action: z.string(), // Keep as string since DB may have actions not in our enum
    timestamp: z.iso.datetime(),
    performedBy: TimecardAuditPerformerSchema.nullable(),
    details: z.unknown().nullable(), // DB may store objects, strings, or other JSON values
});
// Paginated list response
export const TimecardAuditLogListResponseSchema = z.object({
    data: z.array(TimecardAuditLogResponseSchema),
    pagination: z.object({
        total: z.number().int().nonnegative(),
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
    }),
});
// Manager-initiated timecard creation modes
export const ManagerCreateTimecardMode = {
    APPROVE: "approve",
    REQUEST: "request",
};
export const ManagerCreateTimecardModeSchema = z.enum(["approve", "request"]);
export const ManagerCreateTimecardBreakSchema = z.object({
    breakStart: z.iso.datetime(),
    breakEnd: z.iso.datetime(),
    breakType: BreakTypeSchema,
});
export const ManagerCreateTimecardInputSchema = z.object({
    shiftId: z.uuid(),
    mode: ManagerCreateTimecardModeSchema,
    clockIn: z.iso.datetime().optional(),
    clockOut: z.iso.datetime().optional(),
    breaks: z.array(ManagerCreateTimecardBreakSchema).optional(),
    reason: z.string().optional(),
});
// Timecard Adjustment schemas (Phase 2: post-export adjustments)
export const TimecardAdjustmentInputSchema = z.object({
    clockIn: z.iso.datetime().optional(),
    clockOut: z.iso.datetime().optional(),
    breaks: z.array(BreakEditOperationSchema).optional(),
    reason: z.string().min(1).max(1000),
});
export const TimecardAdjustmentResponseSchema = z.object({
    id: z.uuid(),
    timecardId: z.uuid(),
    adjustedBy: z.uuid(),
    regularHoursDelta: z.number(),
    overtimeHoursDelta: z.number(),
    holidayHoursDelta: z.number(),
    reason: z.string(),
    exportedInExportId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    adjustedClockIn: z.iso.datetime().nullable().optional(),
    adjustedClockOut: z.iso.datetime().nullable().optional(),
    adjustedBreaks: z.array(z.any()).nullable().optional(),
});
export const TimecardAdjustmentDetailSchema = TimecardAdjustmentResponseSchema.extend({
    adjusterName: z.string(),
    exportedAt: z.iso.datetime().nullable(),
});
export const GetTimecardAdjustmentsInputSchema = z.object({
    timecardIds: z.array(z.uuid()).min(1).max(100),
});
